import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'

export enum LossRunRequestStatus {
  SENT = 'SENT',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED',
  HUMAN_ACTION_REQUIRED = 'HUMAN_ACTION_REQUIRED',
}

export enum EmailDirection {
  INBOUND = 'INBOUND',
  OUTBOUND = 'OUTBOUND',
}

export interface EmailMessage {
  id: string
  dateCreated: string
  direction: EmailDirection
  fromEmail: string
  toEmail: string
  subject: string
  bodyText: string
  bodyHtml: string
  agentRunId: string | null
}

export interface InsuranceProviderContact {
  id: string
  insuranceName: string
  emails: string[]
}

export enum LossRunClaimCoverageType {
  AUTO_LIABILITY = 'AUTO_LIABILITY',
  AUTO_PHYSICAL_DAMAGE = 'AUTO_PHYSICAL_DAMAGE',
  GENERAL_LIABILITY = 'GENERAL_LIABILITY',
  MOTOR_TRUCK_CARGO = 'MOTOR_TRUCK_CARGO',
  UNKNOWN = 'UNKNOWN',
}

export enum LossRunClaimStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  UNKNOWN = 'UNKNOWN',
}

export interface LossRunClaim {
  id: string
  coverageType: LossRunClaimCoverageType
  incidentDate: string | null
  claimCount: number | null
  paidLoss: string | null
  reserve: string | null
}

export interface LossRunRequest {
  id: string
  dateCreated: string
  dateUpdated: string
  status: LossRunRequestStatus
  dateStart: string
  dateEnd: string
  insuranceProviderContact: InsuranceProviderContact
  emailMessages: EmailMessage[]
  receivedDocument: LossRunDocument | null
  lossRunClaims?: LossRunClaim[]
}

export interface LossRunDocumentAttachment {
  id: string
  file: string
  fileName: string
  fileExtension: string
  fileSize: number
}

export interface LossRunDocument {
  id: string
  dateCreated: string
  attachments: LossRunDocumentAttachment[]
  createdByIsStaff: boolean
  /** What the Documents tab calls it: a name someone set, else the parsed title, else the file name. */
  title: string
}

interface LossRunRequestsResponse {
  requests: LossRunRequest[]
  documents: LossRunDocument[]
  /** Cai is reading loss runs right now, so a claims history that looks empty isn't. */
  isParsing: boolean
}

function getOrgLossRunRequests(orgId: string): Promise<LossRunRequestsResponse> {
  return axios
    .get(`/api/v1/insure/organisations/${orgId}/loss-run-requests/`)
    .then((response) => toCamelCase(response.data) as LossRunRequestsResponse)
}

/**
 * Refetches itself while the backend reports a parse in flight: that runs in a worker and
 * finishes a minute or so after the upload, so a one-shot invalidation at upload time
 * caches the empty result.
 */
export function useOrgLossRunRequests(orgId: string | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.LOSS_RUN_REQUESTS, orgId],
    queryFn: () => getOrgLossRunRequests(orgId!),
    enabled: !!orgId,
    refetchInterval: (query) => (query.state.data?.isParsing ? 10_000 : false),
  })
  return {
    lossRunRequests: data?.requests ?? [],
    documents: data?.documents ?? [],
    isParsing: data?.isParsing ?? false,
    isLoading,
  }
}

/**
 * A request still waiting on an answer — the only state that means a loss run is on its way.
 * HUMAN_ACTION_REQUIRED counts: nobody had an address to send it to, so it is outstanding on us.
 */
export function hasOutstandingLossRunRequest(requests: LossRunRequest[]): boolean {
  return requests.some(
    (request) =>
      request.status === LossRunRequestStatus.SENT ||
      request.status === LossRunRequestStatus.HUMAN_ACTION_REQUIRED
  )
}

/** Every claim parsed out of the loss runs on file. */
export function collectLossRunClaims(requests: LossRunRequest[]): LossRunClaim[] {
  return requests.flatMap((request) => request.lossRunClaims ?? [])
}

/**
 * How many claims those rows add up to. A row can aggregate several (loss runs often
 * summarise by coverage and year); one that gives no count is still one claim, so a
 * populated claims history never totals zero.
 */
export function countLossRunClaims(claims: LossRunClaim[]): number {
  return claims.reduce((total, claim) => total + (claim.claimCount ?? 1), 0)
}

/** Where a loss-run upload was made, for the uploads whose origin changes what happens next. */
export enum LossRunUploadSource {
  /** The page the SMS flow's upload link opens — the only upload that answers the flow's loss-run ask. */
  SMS_LINK = 'sms_link',
}

function uploadLossRunFiles(appId: string, files: File[], source?: LossRunUploadSource) {
  const form = new FormData()
  files.forEach((f) => form.append('file', f))
  if (source) form.append('source', source)
  return axios.post(`/api/v1/insure/applications/${appId}/loss-run-uploads/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export function useUploadLossRunFiles(appId: string | undefined, source?: LossRunUploadSource) {
  return useMutation({
    mutationFn: (files: File[]) => uploadLossRunFiles(appId!, files, source),
  })
}

interface LossRunClaimPatch {
  coverageType?: string
  incidentDate?: string | null
  claimCount?: number | null
  paidLoss?: string | null
  reserve?: string | null
}

function patchLossRunClaim(orgId: string, claimId: string, data: LossRunClaimPatch): Promise<LossRunClaim> {
  return axios
    .patch(`/api/v1/insure/organisations/${orgId}/loss-run-claims/${claimId}/`, toSnakeCase(data))
    .then((r) => toCamelCase(r.data) as LossRunClaim)
}

export function usePatchLossRunClaim(orgId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ claimId, data }: { claimId: string; data: LossRunClaimPatch }) =>
      patchLossRunClaim(orgId, claimId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CacheKeys.LOSS_RUN_REQUESTS, orgId] }),
  })
}
